import type { OuraWorkout } from "../data/ouraTypes";

import { useState, useCallback, useEffect } from "react";

import { getSupabaseClient } from "@/shared/services";

type IntensityMinutes = Record<string, number>;

interface OptimizedWorkoutsData {
  error: null | string;
  intensityMinutes: IntensityMinutes;
  loading: boolean;
  totalCalories: number;
  workouts: OuraWorkout[];
}

// Cache für Workout-Daten
const workoutCache = new Map<string, OptimizedWorkoutsData>();

/**
 * WHAT:
 *   Calculates total calories from workout data.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting calculation logic.
 *
 * @param workoutData - Array of workout data
 *
 * @returns Total calories burned
 *
 */
function calculateTotalCalories(workoutData: OuraWorkout[]): number {
  let totalCalories = 0;
  for (const workout of workoutData) {
    if (typeof workout.calories === "number") {
      totalCalories += workout.calories;
    }
  }
  return totalCalories;
}

/**
 * WHAT:
 *   Checks if workout has required fields for intensity calculation.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting validation logic.
 *
 * @param workout - Workout object to validate
 *
 * @returns True if workout has all required fields
 *
 */
function isValidIntensityWorkout(workout: unknown): workout is OuraWorkout & {
  end_datetime: string;
  intensity: string;
  start_datetime: string;
} {
  return (
    workout !== null &&
    typeof workout === "object" &&
    "intensity" in workout &&
    typeof workout.intensity === "string" &&
    "start_datetime" in workout &&
    typeof workout.start_datetime === "string" &&
    "end_datetime" in workout &&
    typeof workout.end_datetime === "string"
  );
}

/**
 * WHAT:
 *   Calculates intensity minutes from workout data.
 *
 * WHY:
 *   Reduces cognitive complexity by extracting calculation logic.
 *
 * @param workoutData - Array of workout data
 *
 * @returns Object mapping intensity levels to minutes
 *
 */
function calculateIntensityMinutes(workoutData: OuraWorkout[]): IntensityMinutes {
  const intensityMinutes: IntensityMinutes = {};
  for (const workout of workoutData) {
    if (!isValidIntensityWorkout(workout)) {
      continue;
    }

    const start = new Date(workout.start_datetime);
    const end = new Date(workout.end_datetime);
    const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

    if (durationMinutes > 0) {
      const intensity = workout.intensity;
      const currentMinutes = intensityMinutes[intensity];
      intensityMinutes[intensity] = (currentMinutes ?? 0) + durationMinutes;
    }
  }
  return intensityMinutes;
}

/**
 * WHAT:
 *   Optimized Workouts Hook with caching mechanism.
 *
 * WHY:
 *   Caches Supabase data to avoid redundant API calls and optimize performance.
 *
 * FEATURES:
 *   - Caching of Supabase data
 *   - Avoids redundant API calls
 *   - Optimized performance
 *
 * @returns OptimizedWorkoutsData object containing workouts, intensity minutes, total calories, loading state, and error
 *
 */
export const useOptimizedWorkouts = (): OptimizedWorkoutsData => {
  const [data, setData] = useState<OptimizedWorkoutsData>({
    error: null,
    intensityMinutes: {},
    loading: true,
    totalCalories: 0,
    workouts: [],
  });

  const loadWorkouts = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    // Prüfe Cache
    const cachedData = workoutCache.get(today);
    if (cachedData) {
      setData(cachedData);
      return;
    }

    setData((previous) => ({ ...previous, error: null, loading: true }));

    try {
      const supabase = await getSupabaseClient();

      const { data: supabaseData, error: supabaseError } = await supabase
        .from("oura_data")
        .select("workout")
        .eq("date", today)
        .single();

      if (supabaseError) {
        // Fehler beim Laden der Workout-Daten
        const errorData = {
          error: supabaseError.message,
          intensityMinutes: {},
          loading: false,
          totalCalories: 0,
          workouts: [],
        };
        setData(errorData);
        workoutCache.set(today, errorData);
        return;
      }

      if (supabaseData.workout) {
        const rawWorkout = supabaseData.workout as unknown;
        const workoutData: OuraWorkout[] = Array.isArray(rawWorkout)
          ? rawWorkout.filter(
              (w: unknown): w is OuraWorkout =>
                w !== null &&
                typeof w === "object" &&
                "calories" in w &&
                typeof (w as { calories: unknown }).calories === "number",
            )
          : [];

        const totalCalories = calculateTotalCalories(workoutData);
        const intensityMinutes = calculateIntensityMinutes(workoutData);

        const resultData: OptimizedWorkoutsData = {
          error: null,
          intensityMinutes,
          loading: false,
          totalCalories,
          workouts: workoutData,
        };

        setData(resultData);
        workoutCache.set(today, resultData);
      } else {
        const emptyData = {
          error: null,
          intensityMinutes: {},
          loading: false,
          totalCalories: 0,
          workouts: [],
        };
        setData(emptyData);
        workoutCache.set(today, emptyData);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // Fehler beim Laden der Workout-Daten
      const errorData = {
        error: `Fehler: ${message}`,
        intensityMinutes: {},
        loading: false,
        totalCalories: 0,
        workouts: [],
      };
      setData(errorData);
      workoutCache.set(today, errorData);
    }
  }, []);

  useEffect(() => {
    void loadWorkouts();
  }, [loadWorkouts]);

  return data;
};
